import React, { useState, useEffect, useRef } from 'react';
import { renderIcon } from '../../../utils/icons';
import { STATUS_CYCLE, STATUS_COLORS } from '../../../utils/site-constants';

export default function WscChecklistTab({ items, gateStatus, canAdmin, isDark, theme, updateItem, deleteItem, isLocked, isActive, attachments, users, onPaperclipClick }) {
  const [assignDropdownItemId, setAssignDropdownItemId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!assignDropdownItemId) return;
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setAssignDropdownItemId(null);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [assignDropdownItemId]);

  if (!items.length) {
    return <div style={{ fontSize: 13, color: theme.textSecondary, padding: '16px 0', textAlign: 'center' }}>No items yet</div>;
  }

  const sorted = [...items].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  function cycleStatus(item) {
    if (!canAdmin || isLocked) return;
    const idx = STATUS_CYCLE.indexOf(item.status);
    const next = STATUS_CYCLE[(idx + 1) % STATUS_CYCLE.length];
    updateItem && updateItem(item.id, { status: next });
  }

  function handleAssignClick(e, itemId) {
    e.stopPropagation();
    if (!canAdmin || isLocked) return;
    setAssignDropdownItemId(assignDropdownItemId === itemId ? null : itemId);
  }

  function handleAssign(e, itemId, userId) {
    e.stopPropagation();
    updateItem && updateItem(itemId, { assigned_to: userId });
    setAssignDropdownItemId(null);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {sorted.map(item => {
        const isItemComplete = item.status === 'Complete';
        const isBlocked = item.status === 'Blocked';
        const color = STATUS_COLORS[item.status] || '#6b7280';
        const assignedUser = item.assigned_to && users ? users.find(u => u.id === item.assigned_to) : null;
        const avatarColor = assignedUser?.avatar_color || '#6b7280';
        const assignedName = assignedUser?.name || item.assigned_name || null;
        const initial = assignedName ? assignedName.charAt(0).toUpperCase() : '?';

        let icon;
        if (isItemComplete) icon = renderIcon('check-circle', 14, '#4ADE80');
        else if (isLocked) icon = <span style={{ color: '#6b7280', fontSize: 14 }}>○</span>;
        else if (isBlocked) icon = renderIcon('x-circle', 14, '#EF4444');
        else icon = <span style={{ color: color, fontSize: 14 }}>{item.status === 'In Progress' ? '◐' : item.status === 'Awaiting Review' ? '◑' : '○'}</span>;

        return (
          <div
            key={item.id}
            onClick={(e) => { e.stopPropagation(); cycleStatus(item); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 8px', borderRadius: 6,
              cursor: (canAdmin && !isLocked) ? 'pointer' : 'default',
              background: 'transparent',
              transition: 'background 0.1s ease',
              position: 'relative',
            }}
            onMouseEnter={e => {
              if (canAdmin && !isLocked) e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
              const delBtn = e.currentTarget.querySelector('.checklist-delete-btn');
              if (delBtn) delBtn.style.opacity = '0.5';
              const critBtn = e.currentTarget.querySelector('.critical-toggle-btn');
              if (critBtn && !item.is_critical) critBtn.style.opacity = '0.3';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'transparent';
              const delBtn = e.currentTarget.querySelector('.checklist-delete-btn');
              if (delBtn) delBtn.style.opacity = '0';
              const critBtn = e.currentTarget.querySelector('.critical-toggle-btn');
              if (critBtn && !item.is_critical) critBtn.style.opacity = '0';
            }}
          >
            {icon}
            {/* Critical blocker toggle (admin only) */}
            {canAdmin && !isLocked && (
              <span
                onClick={(e) => { e.stopPropagation(); updateItem && updateItem(item.id, { is_critical: !item.is_critical }); }}
                title={item.is_critical ? 'Remove critical blocker' : 'Mark as critical blocker'}
                style={{
                  cursor: 'pointer', flexShrink: 0, fontSize: 12,
                  opacity: item.is_critical ? 1 : 0,
                  transition: 'opacity 0.15s',
                  color: '#EF4444',
                }}
                className="critical-toggle-btn"
              >
                🔥
              </span>
            )}
            <span style={{
              flex: 1, fontSize: 13,
              color: item.is_critical && !isItemComplete ? '#EF4444' : theme.text,
              fontWeight: item.is_critical && !isItemComplete ? 600 : 'normal',
              textDecoration: isItemComplete ? 'line-through' : 'none',
              opacity: isItemComplete ? 0.55 : 1,
              lineHeight: 1.3,
            }}>
              {item.doc}
              {item.is_critical && !isItemComplete && (
                <span style={{
                  fontSize: 10, color: '#EF4444', marginLeft: 4,
                  padding: '1px 5px', borderRadius: 3,
                  background: 'rgba(239,68,68,0.12)',
                  fontWeight: 700,
                }}>
                  CRITICAL
                </span>
              )}
              {item.is_required === false && (
                <span style={{
                  fontSize: 10, color: theme.textSecondary, marginLeft: 4,
                  padding: '1px 4px', borderRadius: 3,
                  background: 'rgba(255,255,255,0.06)',
                }}>
                  opt
                </span>
              )}
            </span>

            {/* Assignee Avatar */}
            <div style={{ flexShrink: 0, position: 'relative' }}>
              {item.assigned_to ? (
                <div
                  onClick={(e) => handleAssignClick(e, item.id)}
                  title={assignedName || 'Assigned'}
                  style={{
                    width: 20, height: 20, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: avatarColor,
                    cursor: (canAdmin && !isLocked) ? 'pointer' : 'default',
                    transition: 'transform 0.1s ease',
                  }}
                  onMouseEnter={e => { if (canAdmin && !isLocked) e.currentTarget.style.transform = 'scale(1.15)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', lineHeight: 1 }}>{initial}</span>
                </div>
              ) : (
                <div
                  onClick={(e) => handleAssignClick(e, item.id)}
                  title="Unassigned — click to assign"
                  style={{
                    width: 20, height: 20, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '1.5px dashed ' + (theme.textSecondary || '#6b7280'),
                    opacity: 0.4,
                    cursor: (canAdmin && !isLocked) ? 'pointer' : 'default',
                    transition: 'opacity 0.1s ease',
                  }}
                  onMouseEnter={e => { if (canAdmin && !isLocked) e.currentTarget.style.opacity = '0.8'; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = '0.4'; }}
                />
              )}

              {/* Assignment Dropdown */}
              {assignDropdownItemId === item.id && (
                <div
                  ref={dropdownRef}
                  style={{
                    position: 'absolute', top: 24, right: 0,
                    background: theme.bgCard,
                    border: '1px solid ' + (theme.border || 'rgba(255,255,255,0.10)'),
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                    zIndex: 100, maxHeight: 200, overflowY: 'auto',
                    padding: '4px 0', minWidth: 160,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Unassign option */}
                  {item.assigned_to && (
                    <div
                      onClick={(e) => handleAssign(e, item.id, null)}
                      style={{
                        padding: '6px 12px', fontSize: 13, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 8,
                        color: '#EF4444', borderBottom: '1px solid ' + (theme.border || 'rgba(255,255,255,0.10)'),
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      {renderIcon('x-circle', 14, '#EF4444')}
                      <span>Unassign</span>
                    </div>
                  )}
                  {/* User list */}
                  {(users || []).map(u => (
                    <div
                      key={u.id}
                      onClick={(e) => handleAssign(e, item.id, u.id)}
                      style={{
                        padding: '6px 12px', fontSize: 13, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 8,
                        color: theme.text,
                        background: u.id === item.assigned_to
                          ? (isDark ? 'rgba(59,130,246,0.12)' : 'rgba(59,130,246,0.08)')
                          : 'transparent',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                      onMouseLeave={e => e.currentTarget.style.background = u.id === item.assigned_to
                        ? 'rgba(59,130,246,0.12)'
                        : 'transparent'}
                    >
                      <span style={{
                        width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                        background: u.avatar_color || '#6b7280',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontSize: 9, fontWeight: 700,
                      }}>
                        {u.name?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                      <span>{u.name}</span>
                      {u.id === item.assigned_to && (
                        <span style={{ marginLeft: 'auto' }}>{renderIcon('check', 12, '#4ADE80')}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {attachments && attachments[item.id] && attachments[item.id].length > 0 && (
              <span
                onClick={(e) => { e.stopPropagation(); onPaperclipClick && onPaperclipClick(item.id); }}
                title={attachments[item.id].length + ' file(s)'}
                style={{
                  cursor: 'pointer', fontSize: 13, flexShrink: 0,
                  opacity: 0.7, transition: 'opacity 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                onMouseLeave={e => e.currentTarget.style.opacity = '0.7'}
              >
                {renderIcon('paperclip', 12)}{attachments[item.id].length > 1 ? attachments[item.id].length : ''}
              </span>
            )}

            {/* Delete button */}
            {canAdmin && !isLocked && (
              confirmDeleteId === item.id ? (
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                  <button
                    onClick={() => { deleteItem && deleteItem(item.id); setConfirmDeleteId(null); }}
                    style={{
                      background: '#EF4444', color: '#fff', border: 'none', borderRadius: 4,
                      fontSize: 10, fontWeight: 600, padding: '2px 8px', cursor: 'pointer',
                    }}
                  >Delete</button>
                  <button
                    onClick={() => setConfirmDeleteId(null)}
                    style={{
                      background: 'rgba(255,255,255,0.08)',
                      color: theme.textSecondary, border: 'none', borderRadius: 4,
                      fontSize: 10, fontWeight: 600, padding: '2px 6px', cursor: 'pointer',
                    }}
                  >Cancel</button>
                </div>
              ) : (
                <span
                  onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(item.id); }}
                  title="Remove task"
                  style={{
                    cursor: 'pointer', flexShrink: 0, opacity: 0, transition: 'opacity 0.15s',
                    padding: '2px 2px',
                  }}
                  className="checklist-delete-btn"
                >
                  {renderIcon('trash-2', 12, '#EF4444')}
                </span>
              )
            )}
          </div>
        );
      })}
    </div>
  );
}
